<template>
  <div class="relative">
    <div ref="map" class="h-64 w-full rounded-xl overflow-hidden" />
    <div
      class="absolute top-4 left-4 right-4 shadow-lg rounded-xxlbg-white lg:max-w-1/2 2xl:max-w-1/4"
    >
      <div class="flex items-center bg-white rounded-full">
        <input
          type="text"
          ref="locationInput"
          class="w-full rounded-full py-3 px-5 z-10 outline-none"
          @keyup="useInputKeyUpLocation"
          @keyup.enter="useInputChangeLocation"
          :placeholder="address ? address : 'Zoek je bezorglocatie'"
        />

        <div v-if="locationLoading" class="pr-5 py-3 h-full flex items-center">
          <span
            :class="[
              'whitespace-no-wrap mr-2 text-gray-600 overflow-hidden transition-all ease-in-out duration-300',
              initialLocationLoading ? 'max-w-screen-100' : 'max-w-screen-0',
            ]"
            >locatie zoeken...</span
          >
          <ClipLoader color="#000" size="1.2rem" class="flex items-center" />
        </div>
        <button
          @click="useCurrentLocation"
          v-else
          type="button"
          class="px-5 py-3 h-full outline-none focus:outline-none"
        >
          <Icon name="map-pin" color="grey" />
        </button>
      </div>
      <button
        v-if="autocompleteResult"
        @click="useAutoCompleteLocation"
        type="button"
        class="px-5 pb-3 text-gray-700 focus:outline-none w-full text-left"
      >
        {{ autocompleteResult }}
      </button>
    </div>
  </div>
</template>

<script>
const ClipLoader = require("vue-spinner/src/ClipLoader.vue").default;

import Icon from "./Icon.vue";

import {
  mapsLoader,
  distanceServiceOptions,
  locationPermission,
} from "../utils";

const mapStyle = [
  {
    featureType: "poi",
    stylers: [{ visibility: "off" }],
  },
];

export default {
  name: "DeliveryMap",
  data() {
    return {
      // services from the google api
      googleService: undefined,
      geocoderService: undefined,
      distanceService: undefined,
      autocompleteService: undefined,
      mapService: undefined,
      markerService: undefined,

      // data
      initialLocationLoading: false,
      locationLoading: false,
      coords: null,
      address: null,
      autocompleteResult: null,
    };
  },
  props: {
    origin: [String, Object],
    value: String,
  },
  components: {
    ClipLoader,
    Icon,
  },
  async mounted() {
    /**
     * Defines all needed services and stores them in states
     */
    const google = await mapsLoader.load();
    this.googleService = await google;
    this.geocoderService = new google.maps.Geocoder();
    this.distanceService = new google.maps.DistanceMatrixService();
    this.mapService = new google.maps.Map(this.$refs.map, {
      center: { lat: -34.397, lng: 150.644 },
      zoom: 13,
      mapTypeId: "roadmap",
      disableDefaultUI: true,
    });
    this.autocompleteService = new google.maps.places.AutocompleteService(
      this.mapService,
      {
        componentRestrictions: { country: "be" },
        fields: ["name", "geometry"],
      }
    );
    this.markerService = new google.maps.Marker({
      map: this.mapService,
    });

    /** Apply certain styles to map */
    this.mapService.setOptions({ styles: mapStyle });

    /** Add click event listener for map */
    this.mapService.addListener("click", this.useClickLocation);

    /** Pinpoint current location as default address */
    // this.useCurrentLocation()
  },
  methods: {
    /**
     * Places a marker on the map
     * @param latLng Google API latLng object
     */
    async setMarker(latLng, address) {
      const result = address
        ? address
        : await this.reverseGeocoderAsync(latLng);
      this.emitResponse(await result, latLng);
    },

    /**
     * Get a location by place description.
     * Description can be provided in the form of a input field for example
     * @param {string} query Description of place
     */
    getPlacesByQueryAsync(query) {
      const self = this;
      return new Promise((resolve, reject) => {
        self.autocompleteService.getQueryPredictions(
          {
            input: query,
            fields: ["name", "geometry"],
          },
          (resp) => {
            if (resp) resolve(resp[0].description);
            else reject(resp);
          }
        );
      });
    },

    /**
     * @param {Object} event Is provided by default if implemented in keyp-up event of input field
     */
    async useInputKeyUpLocation({ target: { value } }) {
      if (value !== "") {
        const result = await this.getPlacesByQueryAsync(value);
        this.autocompleteResult = await result;
      }
    },

    /**
     * Returns the address of a location based on coordinates
     * @param latLng Google API latLng object
     */
    reverseGeocoderAsync(latLng) {
      return new Promise((resolve, reject) => {
        this.geocoderService
          .geocode({ location: latLng })
          .then((resp) => {
            if (resp) {
              const address = resp.results[0].formatted_address;
              resolve(address);
            } else reject(resp);
          })
          .catch(reject);
      });
    },

    /**
     * Returns the coordinates of a location based on an address
     * @param {string} address The address whose coordinates are to be found
     */
    geocoderAddressAsync(address) {
      return new Promise((resolve, reject) => {
        this.geocoderService
          .geocode({ address })
          .then((resp) => {
            const latLng = resp.results[0].geometry.location;
            resolve(latLng);
          })
          .catch(reject);
      });
    },

    async useInputChangeLocation() {
      this.useAutoCompleteLocation();
    },

    useCurrentLocation() {
      const google = this.googleService;
      const self = this;

      this.autocompleteResult = null;
      this.locationLoading = true;

      locationPermission(
        ({ coords: { latitude, longitude } }) => {
          self.locationLoading = false;
          self.initialLocationLoading = false;
          self.coords = { latitude, longitude };

          const position = new google.maps.LatLng(latitude, longitude);

          this.setMarker(position);
        },
        (err) => {
          alert(
            "Enable location permissions to select an address\n" +
              `Error state: ${err.state}`
          );
          this.locationLoading = false;
        }
      );
    },

    useClickLocation(e) {
      const position = e.latLng;
      this.coords = { ...position };

      this.setMarker(position);
    },

    async useAutoCompleteLocation() {
      const latLng = await this.geocoderAddressAsync(this.autocompleteResult);
      this.$refs.locationInput.value = this.autocompleteResult;

      this.emitResponse(this.autocompleteResult, latLng);
      this.autocompleteResult = null;
    },

    /**
     * Returns the distance between 2 points in kilometers (being A the location of the store, and B the location of the customer)
     * @param destinationLatLng Google API latLng object
     */
    calcDrivingTime(destinationLatLng) {
      const { latitude: lat, longitude: lng } = this.origin;
      const originCoords = new this.googleService.maps.LatLng({ lat, lng });

      return new Promise((resolve, reject) => {
        try {
          this.distanceService.getDistanceMatrix(
            {
              origins: [originCoords],
              destinations: [destinationLatLng],
              ...distanceServiceOptions,
            },
            (resp) => {
              if (!resp) reject(resp);
              else {
                const {
                  rows: [
                    {
                      elements: [{ status }],
                    },
                  ],
                } = resp;
                if (status === "ZERO_RESULTS") reject(resp);
                else {
                  const {
                    rows: [
                      {
                        elements: [
                          {
                            distance: { value },
                          },
                        ],
                      },
                    ],
                  } = resp;
                  const kilometers = value / 1000;
                  resolve(kilometers);
                }
              }
            }
          );
        } catch (error) {
          reject(error);
        }
      });
    },

    /**
     * Triggers an event that can be used via the `change` event prop
     * @param {string} address The address that is chosen by the user
     * @param layLng Google API latLng object - needed for certain calculations
     */
    async emitResponse(address, latLng) {
      this.address = address;
      const coords = latLng.toJSON();

      try {
        const transitDistance = await this.calcDrivingTime(latLng);
        this.$emit("change", {
          address,
          coords,
          transitDistance,
        });

        this.$refs.locationInput.value = address;
        this.markerService.setPosition(latLng);
        this.mapService.panTo(latLng);
        this.mapService.setZoom(16);
      } catch (error) {
        alert("We couldn't find any result for this location.");
      }
    },
  },
};
</script>
